import React, { useEffect, useState } from "react";

const formatDate = (v) => {
  if (!v) return "—";
  try {
    return new Date(v).toLocaleDateString("es-MX", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return String(v);
  }
};

const formatMXN = (n) =>
  `$${Number(n || 0).toLocaleString("es-MX", { minimumFractionDigits: 2 })}`;

const STATUS_STYLES = {
  paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
  delivered: "bg-emerald-50 text-emerald-700 border-emerald-200",
  shipped: "bg-blue-50 text-blue-700 border-blue-200",
  in_transit: "bg-blue-50 text-blue-700 border-blue-200",
  confirmed: "bg-blue-50 text-blue-700 border-blue-200",
  issued: "bg-amber-50 text-amber-800 border-amber-200",
  pending: "bg-amber-50 text-amber-800 border-amber-200",
  overdue: "bg-red-50 text-red-700 border-red-200",
  rejected: "bg-red-50 text-red-700 border-red-200",
  cancelled: "bg-slate-100 text-slate-600 border-slate-200",
  new: "bg-slate-100 text-slate-700 border-slate-200",
};

const STATUS_LABEL_ES = {
  paid: "Pagada",
  delivered: "Entregado",
  shipped: "Enviado",
  in_transit: "En tránsito",
  confirmed: "Confirmado",
  issued: "Emitida",
  pending: "Pendiente",
  overdue: "Vencida",
  rejected: "Rechazada",
  cancelled: "Cancelado",
  new: "Nueva",
};

export const StatusPill = ({ value, testId }) => {
  const cls = STATUS_STYLES[value] || STATUS_STYLES.new;
  const label = STATUS_LABEL_ES[value] || value || "—";
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center px-2.5 py-1 text-[10px] tracking-[0.18em] uppercase font-bold border ${cls}`}
      style={{ borderRadius: 4 }}
    >
      {label}
    </span>
  );
};

export const PageHeader = ({ eyebrow, title, subtitle, action }) => (
  <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
    <div>
      <p className="text-xs tracking-[0.24em] uppercase font-bold text-[var(--icor-blue)] eyebrow-line">
        {eyebrow}
      </p>
      <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tighter mt-3 text-[var(--icor-navy)]">
        {title}
      </h1>
      {subtitle && (
        <p className="text-sm text-slate-500 mt-2 max-w-xl">{subtitle}</p>
      )}
    </div>
    {action}
  </div>
);

export const DataTable = ({ columns, rows, loading, emptyMsg, testId }) => (
  <div
    className="bg-white border border-slate-200 overflow-x-auto"
    style={{ borderRadius: 4 }}
    data-testid={testId}
  >
    <table className="w-full text-sm">
      <thead>
        <tr className="bg-slate-50 border-b border-slate-200">
          {columns.map((c) => (
            <th
              key={c.key}
              className="text-left px-4 py-3 text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500"
            >
              {c.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {loading ? (
          <tr>
            <td
              colSpan={columns.length}
              className="px-4 py-8 text-center text-slate-400"
            >
              Cargando…
            </td>
          </tr>
        ) : rows.length === 0 ? (
          <tr>
            <td
              colSpan={columns.length}
              className="px-4 py-8 text-center text-slate-400"
            >
              {emptyMsg || "Sin registros"}
            </td>
          </tr>
        ) : (
          rows.map((r, i) => (
            <tr
              key={r.id || i}
              className="border-b border-slate-100 hover:bg-slate-50"
              data-testid={`${testId}-row-${r.id || i}`}
            >
              {columns.map((c) => (
                <td key={c.key} className="px-4 py-3 text-slate-700">
                  {c.render ? c.render(r) : r[c.key] ?? "—"}
                </td>
              ))}
            </tr>
          ))
        )}
      </tbody>
    </table>
  </div>
);

export const useFetchList = (api, path) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let active = true;
    setLoading(true);
    api
      .get(path)
      .then((res) => {
        if (active) setItems(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => {
        if (active) setItems([]);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [api, path]);
  return { items, loading };
};

export { formatDate, formatMXN };
